import React, { useState } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#4c7b25] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`} className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}

const gcd = (x, y) => { x = Math.abs(x); y = Math.abs(y); while (y) { [x, y] = [y, x % y]; } return x || 1; };

/* Visuals for ratio A:B */
function RatioPie({ a, b }) {
  const total = Math.abs(a) + Math.abs(b);
  if (!total) return null;
  const angle = (a / total) * 2 * Math.PI;
  const cx = 60, cy = 60, r = 55;
  const x = cx + r * Math.sin(angle);
  const y = cy - r * Math.cos(angle);
  const large = angle > Math.PI ? 1 : 0;
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <circle cx={cx} cy={cy} r={r} fill="#1e40af" />
      <path d={`M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${large} 1 ${x},${y} Z`} fill="#3b82f6" />
      <text x={cx - 15} y={cy + 4} fontSize="11" fill="#fff" textAnchor="middle">{a}</text>
      <text x={cx + 20} y={cy + 4} fontSize="11" fill="#fff" textAnchor="middle">{b}</text>
    </svg>
  );
}

function RatioBar({ a, b }) {
  const total = Math.abs(a) + Math.abs(b);
  if (!total) return null;
  const wa = (Math.abs(a) / total) * 100;
  return (
    <div className="w-full max-w-md">
      <div className="flex h-10 rounded overflow-hidden border border-slate-300">
        <div className="bg-slate-800 flex items-center justify-center text-white text-xs font-semibold" style={{ width: `${wa}%` }}>Left {a}</div>
        <div className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold" style={{ width: `${100 - wa}%` }}>Right {b}</div>
      </div>
    </div>
  );
}

function RatioRect({ w, h }) {
  const W = Math.max(1, Math.abs(w)), H = Math.max(1, Math.abs(h));
  const scale = 100 / Math.max(W, H);
  const pxW = W * scale, pxH = H * scale;
  return (
    <div className="flex items-end gap-2">
      <div className="border-2 border-blue-600 bg-blue-100" style={{ width: pxW, height: pxH, transition: 'width 300ms ease, height 300ms ease' }} />
      <div className="text-xs text-slate-600">
        <div>Width {w}</div>
        <div>Height {h}</div>
      </div>
    </div>
  );
}

/* Section 1: A:B = C:D solver */
function ProportionSection() {
  const [a, setA] = useState(3);
  const [b, setB] = useState(4);
  const [c, setC] = useState(600);
  const [d, setD] = useState('');
  const [blank, setBlank] = useState('d'); // which one to compute
  const [result, setResult] = useState(null);

  const calc = () => {
    const A = +a || 0, B = +b || 0, C = +c || 0, D = +d || 0;
    let missing, value;
    if (blank === 'a') { if (!D) return; value = (B * C) / D; missing = 'A'; }
    else if (blank === 'b') { if (!C) return; value = (A * D) / C; missing = 'B'; }
    else if (blank === 'c') { if (!B) return; value = (A * D) / B; missing = 'C'; }
    else { if (!A) return; value = (B * C) / A; missing = 'D'; }
    // Recompute the "resolved" a:b for visualizations
    let aV = A, bV = B, cV = C, dV = D;
    if (missing === 'A') aV = value;
    if (missing === 'B') bV = value;
    if (missing === 'C') cV = value;
    if (missing === 'D') dV = value;
    const g1 = gcd(Math.round(aV), Math.round(bV));
    setResult({
      missing, value,
      a: aV, b: bV, c: cV, d: dV,
      simA: Math.round(aV) / g1, simB: Math.round(bV) / g1,
      normOne: bV !== 0 ? aV / bV : null,
      normOneB: aV !== 0 ? bV / aV : null,
      pctA: aV + bV !== 0 ? (aV / (aV + bV)) * 100 : 0,
      pctB: aV + bV !== 0 ? (bV / (aV + bV)) * 100 : 0
    });
  };
  const clear = () => { setA(0); setB(0); setC(0); setD(''); setResult(null); };

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900">Ratio Calculator</h3>
      <p className="text-sm text-slate-600 mt-1">Please provide any three values below to calculate the fourth in the ratio <b>A : B = C : D</b>.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" value={a} onChange={e => setA(e.target.value)} className={`w-24 px-3 py-2 rounded border bg-white ${blank === 'a' ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`} />
          <span className="text-xl font-semibold text-slate-600">:</span>
          <input type="number" value={b} onChange={e => setB(e.target.value)} className={`w-24 px-3 py-2 rounded border bg-white ${blank === 'b' ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`} />
          <span className="text-xl font-semibold text-slate-600">=</span>
          <input type="number" value={c} onChange={e => setC(e.target.value)} className={`w-24 px-3 py-2 rounded border bg-white ${blank === 'c' ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`} />
          <span className="text-xl font-semibold text-slate-600">:</span>
          <input type="number" value={d} onChange={e => setD(e.target.value)} className={`w-24 px-3 py-2 rounded border bg-white ${blank === 'd' ? 'border-blue-500 bg-blue-50' : 'border-slate-300'}`} />
        </div>
        <div className="mt-3 text-sm text-slate-600">
          Solve for: {['a', 'b', 'c', 'd'].map(k => (
            <label key={k} className="ml-3 inline-flex items-center gap-1"><input type="radio" name="blank" checked={blank === k} onChange={() => setBlank(k)} /> {k.toUpperCase()}</label>
          ))}
        </div>
        <CalcButtons testId="ratio-prop" onCalc={calc} onClear={clear} />
      </div>
      {result && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
          <div className="text-xl font-bold text-blue-700 mb-2 font-mono">{result.a} : {result.b} = {result.c} : {result.d}</div>
          <table className="w-full text-sm border border-slate-200">
            <tbody>
              <tr><td className="px-3 py-2">Simplified ratio</td><td className="px-3 py-2 text-right font-mono">{result.simA} : {result.simB}</td></tr>
              {result.normOne !== null && <tr className="bg-slate-50"><td className="px-3 py-2">Normalized to 1 : x</td><td className="px-3 py-2 text-right font-mono">1 : {result.normOne.toFixed(6)}</td></tr>}
              {result.normOneB !== null && <tr><td className="px-3 py-2">Normalized to x : 1</td><td className="px-3 py-2 text-right font-mono">{result.normOneB.toFixed(6)} : 1</td></tr>}
              <tr className="bg-slate-50"><td className="px-3 py-2">Percentage breakdown</td><td className="px-3 py-2 text-right font-mono">{result.pctA.toFixed(4)}% : {result.pctB.toFixed(4)}%</td></tr>
            </tbody>
          </table>

          <h4 className="mt-4 font-bold text-slate-900">Visual Representations of the Ratio {Math.round(result.a)} : {Math.round(result.b)}</h4>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="flex flex-col items-center"><RatioPie a={result.simA} b={result.simB} /><span className="text-xs text-slate-500 mt-1">Pie Chart</span></div>
            <div className="flex flex-col items-center"><RatioBar a={Math.round(result.a)} b={Math.round(result.b)} /></div>
            <div className="flex flex-col items-center"><RatioRect w={Math.round(result.b)} h={Math.round(result.a)} /></div>
          </div>

          <StepsPanel steps={[
            `Cross-multiply: A × D = B × C`,
            `${result.missing} = ${result.missing === 'A' ? 'B × C / D' : result.missing === 'B' ? 'A × D / C' : result.missing === 'C' ? 'A × D / B' : 'B × C / A'} = <b>${(+result.value).toFixed(6)}</b>`
          ]} />
        </div>
      )}
    </div>
  );
}

/* Section 2: Ratio Scaling */
function ScalingSection() {
  const [a, setA] = useState(250);
  const [b, setB] = useState(280);
  const [factor, setFactor] = useState(2.5);
  const [mode, setMode] = useState('shrink');
  const [result, setResult] = useState(null);
  const calc = () => {
    const f = +factor || 1;
    const scale = mode === 'shrink' ? 1 / f : f;
    setResult({ a: +a * scale, b: +b * scale, scale, mode });
  };
  const clear = () => { setA(0); setB(0); setFactor(0); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Ratio Scaling Calculator</h3>
      <p className="text-sm text-slate-600 mt-1">Enlarge or shrink a ratio by a given factor.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" value={a} onChange={e => setA(e.target.value)} className="w-24 px-3 py-2 rounded border border-slate-300 bg-white" />
          <span className="text-xl font-semibold text-slate-600">:</span>
          <input type="number" value={b} onChange={e => setB(e.target.value)} className="w-24 px-3 py-2 rounded border border-slate-300 bg-white" />
          <select value={mode} onChange={e => setMode(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white">
            <option value="shrink">Shrink by</option>
            <option value="enlarge">Enlarge by</option>
          </select>
          <input type="number" step="any" value={factor} onChange={e => setFactor(e.target.value)} className="w-24 px-3 py-2 rounded border border-slate-300 bg-white" />
          <span className="text-sm text-slate-700">times</span>
        </div>
        <CalcButtons testId="ratio-scale" onCalc={calc} onClear={clear} />
      </div>
      {result && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
          <div className="text-xl font-bold text-blue-700 mb-3 font-mono">
            {a}:{b} {result.mode === 'shrink' ? '÷' : '×'} {factor} = <span className="text-[#4c7b25]">{result.a.toFixed(4)} : {result.b.toFixed(4)}</span>
          </div>
          <div className="text-xs text-slate-500 mb-2">{Math.round(+a)}×{Math.round(+b)} and {Math.round(result.a)}×{Math.round(result.b)} — Size Visualization:</div>
          <div className="relative bg-slate-50 rounded p-3" style={{ minHeight: 200 }}>
            <div className="bg-blue-300 border border-blue-500" style={{ width: Math.min(400, +a) + 'px', height: Math.min(200, +b * 0.6) + 'px', transition: 'width 300ms ease, height 300ms ease' }} />
            <div className="absolute top-3 left-3 bg-blue-800 border border-blue-900" style={{ width: Math.min(400, result.a) + 'px', height: Math.min(200, result.b * 0.6) + 'px', transition: 'width 300ms ease, height 300ms ease' }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* Section 3: Simplify Ratios */
function SimplifySection() {
  const [terms, setTerms] = useState('12:18:24');
  const [result, setResult] = useState(null);
  const calc = () => {
    const parts = terms.split(':').map(t => Number(t.trim())).filter(n => Number.isFinite(n) && n !== 0);
    if (parts.length < 2) { setResult({ err: 'Please provide at least two numbers separated by ":"' }); return; }
    const g = parts.reduce((acc, v) => gcd(acc, Math.round(v)), Math.round(parts[0]));
    setResult({ original: parts, simplified: parts.map(p => p / g), gcd: g });
  };
  const clear = () => { setTerms(''); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Simplify a Multi-Term Ratio</h3>
      <p className="text-sm text-slate-600 mt-1">Reduce an A:B:C:… ratio to its simplest form.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm">Ratio:</span>
          <input value={terms} onChange={e => setTerms(e.target.value)} placeholder="e.g. 12:18:24" className="w-64 px-3 py-2 rounded border border-slate-300 bg-white font-mono" />
        </div>
        <CalcButtons testId="ratio-simplify" onCalc={calc} onClear={clear} />
      </div>
      {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
      {result && !result.err && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
          <div className="text-xl font-bold text-blue-700 font-mono">{result.original.join(' : ')} = <span className="text-[#4c7b25]">{result.simplified.join(' : ')}</span></div>
          <div className="text-sm text-slate-600 mt-1">GCD = {result.gcd}</div>
        </div>
      )}
    </div>
  );
}

export default function RatioCalc() {
  const [showWork, setShowWork] = useState(false);
  return (
    <CalcShell look="cnet" title="Ratio Calculator" subtitle="Modify the values and click the Calculate button to use">
      <ProportionSection />
      <ScalingSection />
      <SimplifySection />
      <div className="mt-6">
        <button onClick={() => setShowWork(v => !v)} data-testid="ratio-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working (Cross-Multiplication)'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="ratio-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">How the ratio solver works</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Two ratios are equal</b> when the cross-products are equal: <span className="font-mono">A : B = C : D ⇔ A × D = B × C</span></li>
              <li><b>Solving for the unknown</b> (marked in blue above): rearrange to isolate it. E.g. to find D: <span className="font-mono">D = B × C / A</span>.</li>
              <li><b>Simplify a ratio</b> by dividing every term by the greatest common divisor (GCD). E.g. 12 : 18 : 24 has GCD 6, so it simplifies to <span className="font-mono">2 : 3 : 4</span>.</li>
              <li><b>Scale a ratio</b> by multiplying (enlarge) or dividing (shrink) every term by the same factor.</li>
              <li><b>Normalize</b> a ratio to the form <span className="font-mono">1 : x</span> by dividing both terms by A, or to <span className="font-mono">x : 1</span> by dividing by B.</li>
              <li><b>Percent breakdown</b>: each term / sum × 100%.</li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
